import React, { useEffect, useRef, useState } from "react";
import { useSpring, animated, to } from "@react-spring/web";
import CookieOutlinedIcon from "@mui/icons-material/CookieOutlined";
import dinoImage from "../assets/images/Dino.svg";
import dino1Image from "../assets/images/Dino1.svg";
import dino2Image from "../assets/images/Dino2.svg";
import dino3Image from "../assets/images/Dino3.svg";
import dino4Image from "../assets/images/Dino4.svg";
import asteroidImage from "../assets/images/asteroidWCookie.svg";
import PeekingManifesto from "./PeekingManifesto";
import CookieBackground from "./CookieBackground";

const OH_SHIT_ITEMS = [
  { src: dino1Image, w: 216, h: 106, scale: 1.6 },
  { src: dino2Image, w: 202, h: 108, scale: 1.6 },
  { src: dino3Image, w: 175, h: 124, scale: 1.6 },
  { src: dino4Image, w: 367, h: 101, scale: 1.5 },
];
const DINO_WIDTH_PX = 76;
const WORLD_SHIFT_UP_PX = 82;
const WORLD_TEXT_OFFSET_PX = 0;
const COOKIE_PRESS_HOLD_MS = 130;
const COOKIE_RELEASE_SETTLE_MS = 190;
const LINE2_FRESH = "on a fresh new window.";
const LINE2_TARGET = "with curiosity.";
const CIPHER_GLYPHS =
  "ABCDEFGHJKLMNPQRSTUVWXYZ23456789¥$#%&*§?Ø∆¤";

/**
 * Curtain timeline (from mount):
 * - ~300ms: “Before we begin” + headline springs in
 * - CURTAIN_ENCRYPT_START_MS: second line encrypts → LINE2_TARGET
 * - CURTAIN_UNVEIL_AT_MS: cookie UI begins (`show`) — same overall beat as pre-encryption
 * - Brief overlap only: tagline fades TAGLINE_HOLD_AFTER_UNVEIL_MS after unveil so
 *   “with curiosity.” doesn’t sit over the cookie screen for long.
 *
 * After `show`, key delays: world 520ms, heading 1040ms, subtitle 1360ms,
 * accept 1720ms, decline 2000ms, bottom 2320ms; manifesto after those.
 */
const CURTAIN_ENCRYPT_START_MS = 1180;
const CURTAIN_ENCRYPT_DURATION_MS = 1020;
const CURTAIN_UNVEIL_AT_MS = 3000;
/** Short crossfade with cookie layer; keep total page rhythm unchanged */
const TAGLINE_HOLD_AFTER_UNVEIL_MS = 640;
const MANIFESTO_ENTRANCE_DELAY_MS = 2600;

function randomCipherGlyph(): string {
  const i = Math.floor(Math.random() * CIPHER_GLYPHS.length);
  return CIPHER_GLYPHS[i] ?? "?";
}

function buildCipherString(target: string, progress: number): string {
  const n = target.length;
  let out = "";
  for (let i = 0; i < n; i++) {
    const ch = target[i];
    if (ch === " ") {
      out += " ";
      continue;
    }
    const t = n <= 1 ? 1 : i / (n - 1);
    const unlock = 0.06 + t * 0.9;
    if (progress >= unlock) out += ch;
    else out += randomCipherGlyph();
  }
  return out;
}

type CookieAction = "accept" | "decline";
type ButtonPhase = "idle" | "accept-press" | "accept-release" | "decline-press" | "decline-release";

interface CookieConsentProps {
  onAccept: () => void;
  onDecline: () => void;
}

const CookieConsent: React.FC<CookieConsentProps> = ({
  onAccept,
  onDecline,
}) => {
  const [buttonPhase, setButtonPhase] = useState<ButtonPhase>("idle");
  const [dinoIndex, setDinoIndex] = useState(0);
  const [dinoTransitioning, setDinoTransitioning] = useState(false);
  const [phase, setPhase] = useState<"curtain" | "unveil">("curtain");
  const [line2Phase, setLine2Phase] = useState<"fresh" | "cipher" | "final">(
    "fresh"
  );
  const [cipherLine, setCipherLine] = useState("");
  const [taglineHidden, setTaglineHidden] = useState(false);
  const encRafRef = useRef<number>(0);
  const pressTimeoutRef = useRef<number | null>(null);
  const releaseTimeoutRef = useRef<number | null>(null);

  // Tagline stays visible after unveil; fades only after TAGLINE_HOLD_AFTER_UNVEIL_MS
  const curtainTaglineSpring = useSpring({
    opacity: taglineHidden ? 0 : 1,
    y: taglineHidden ? -8 : 0,
    config: { tension: 58, friction: 30 },
    delay: taglineHidden ? 0 : phase === "curtain" ? 300 : 0,
  });

  useEffect(() => {
    const tUnveil = window.setTimeout(() => {
      setPhase("unveil");
    }, CURTAIN_UNVEIL_AT_MS);
    return () => window.clearTimeout(tUnveil);
  }, []);

  useEffect(() => {
    if (phase !== "unveil") return;
    const t = window.setTimeout(() => {
      setTaglineHidden(true);
    }, TAGLINE_HOLD_AFTER_UNVEIL_MS);
    return () => window.clearTimeout(t);
  }, [phase]);

  useEffect(() => {
    const tStart = window.setTimeout(() => {
      setLine2Phase("cipher");
      setCipherLine(buildCipherString(LINE2_TARGET, 0));
      const t0 = performance.now();
      const duration = CURTAIN_ENCRYPT_DURATION_MS;
      const step = (now: number) => {
        const elapsed = now - t0;
        const p = Math.min(1, elapsed / duration);
        setCipherLine(buildCipherString(LINE2_TARGET, p));
        if (p < 1) {
          encRafRef.current = requestAnimationFrame(step);
        } else {
          setCipherLine(LINE2_TARGET);
          setLine2Phase("final");
        }
      };
      encRafRef.current = requestAnimationFrame(step);
    }, CURTAIN_ENCRYPT_START_MS);
    return () => {
      window.clearTimeout(tStart);
      cancelAnimationFrame(encRafRef.current);
    };
  }, []);

  const show = phase === "unveil";

  // Grid fades in early — sits behind the curtain copy
  const gridEarlySpring = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    delay: 480,
    config: { tension: 42, friction: 38 },
  });

  const worldSpring = useSpring({
    opacity: show ? 1 : 0,
    y: show ? 0 : 50,
    config: { tension: 70, friction: 26 },
    // Dino + asteroid arrive first once curtain lifts
    delay: show ? 520 : 0,
  });

  const headingSpring = useSpring({
    opacity: show ? 1 : 0,
    y: show ? 0 : 22,
    rotate: show ? 0 : -0.5,
    config: { tension: 48, friction: 38 },
    delay: show ? 1040 : 0,
  });

  const subtitleSpring = useSpring({
    opacity: show ? 1 : 0,
    y: show ? 0 : 12,
    config: { tension: 44, friction: 38 },
    delay: show ? 1360 : 0,
  });

  const acceptSpring = useSpring({
    opacity: show ? 1 : 0,
    y: show ? 0 : 10,
    scale: 1,
    config: { tension: 44, friction: 38 },
    delay: show ? 1720 : 0,
  });

  const declineSpring = useSpring({
    opacity: show ? 1 : 0,
    y: show ? 0 : 10,
    config: { tension: 44, friction: 38 },
    delay: show ? 2000 : 0,
  });

  const bottomTextSpring = useSpring({
    opacity: show ? 1 : 0,
    y: show ? 0 : 10,
    config: { tension: 42, friction: 40 },
    delay: show ? 2320 : 0,
  });

  useEffect(() => {
    return () => {
      if (pressTimeoutRef.current !== null) {
        window.clearTimeout(pressTimeoutRef.current);
      }
      if (releaseTimeoutRef.current !== null) {
        window.clearTimeout(releaseTimeoutRef.current);
      }
    };
  }, []);

  const queueAction = (action: CookieAction) => {
    if (buttonPhase !== "idle") return;

    setButtonPhase(`${action}-press`);
    pressTimeoutRef.current = window.setTimeout(() => {
      pressTimeoutRef.current = null;
      setButtonPhase(`${action}-release`);

      releaseTimeoutRef.current = window.setTimeout(() => {
        releaseTimeoutRef.current = null;
        setButtonPhase("idle");

        if (action === "accept") {
          onAccept();
          return;
        }
        onDecline();
      }, COOKIE_RELEASE_SETTLE_MS);
    }, COOKIE_PRESS_HOLD_MS);
  };

  const isAnimating = buttonPhase !== "idle";
  const isAcceptPressed = buttonPhase === "accept-press";
  const isAcceptReleasing = buttonPhase === "accept-release";
  const isDeclinePressed = buttonPhase === "decline-press";
  const isDeclineReleasing = buttonPhase === "decline-release";

  const autoTimerRef = useRef<number | null>(null);

  const advanceDino = () => {
    setDinoTransitioning(true);
    setTimeout(() => {
      setDinoIndex((prev) => (prev + 1) % OH_SHIT_ITEMS.length);
      setTimeout(() => setDinoTransitioning(false), 300);
    }, 200);
  };

  const resetAutoTimer = () => {
    if (autoTimerRef.current !== null) window.clearInterval(autoTimerRef.current);
    autoTimerRef.current = window.setInterval(() => {
      advanceDino();
    }, 4000);
  };

  useEffect(() => {
    resetAutoTimer();
    return () => {
      if (autoTimerRef.current !== null) window.clearInterval(autoTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDinoClick = () => {
    if (dinoTransitioning) return;
    advanceDino();
    resetAutoTimer();
  };

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col overflow-hidden">

      {/* Phase 1: Theatre curtain — tagline stays up after unveil, then fades */}
      <div
        className="fixed inset-0 z-[60] flex items-start justify-center pointer-events-none"
        style={{ paddingTop: "33vh" }}
      >
        <animated.div
          className="text-center px-8 max-w-[min(92vw,520px)] sm:max-w-[min(94vw,640px)]"
          style={{
            opacity: curtainTaglineSpring.opacity,
            transform: curtainTaglineSpring.y.to((y) => `translateY(${y}px)`),
          }}
        >
          <p
            className="gilroy-regular text-white/40 tracking-[0.22em] uppercase"
            style={{ fontSize: "clamp(11px, 1.1vw, 13px)", marginBottom: 18 }}
          >
            Before we begin
          </p>
          <p
            className="text-white shiny-text"
            style={{
              fontFamily: "'Kurdis Bold', sans-serif",
              fontSize: "clamp(30px, 3.1vw, 44px)",
              lineHeight: 1.05,
              letterSpacing: "-0.03em",
              textTransform: "none",
            }}
          >
            <span className="inline-block">Best experienced</span>
            <br />
            <span
              className={`inline-block whitespace-normal sm:whitespace-nowrap ${
                line2Phase === "cipher" ? "intro-curtain-cipher" : ""
              }`}
            >
              {line2Phase === "fresh"
                ? LINE2_FRESH
                : line2Phase === "cipher"
                  ? cipherLine
                  : LINE2_TARGET}
            </span>
          </p>
        </animated.div>
      </div>

      {/* Phase 2: Cookie consent page */}

      {/* Brick grid — starts well before curtain lifts; reads behind “Best experienced…” */}
      <animated.div
        className="absolute inset-0 z-0"
        style={{ opacity: gridEarlySpring.opacity }}
      >
        <CookieBackground />
      </animated.div>
      {/* Main content */}
      <div
        className="relative z-10 flex flex-1 flex-col items-center justify-start px-6"
        style={{ paddingTop: "clamp(72px, 8vh, 104px)", pointerEvents: "none" }}
      >
        <div className="w-full max-w-[520px] text-center text-white">
          <animated.h2
            className="mb-4"
            style={{
              fontFamily: "'Kurdis Bold', sans-serif",
              fontSize: "clamp(44px, 4.2vw, 56px)",
              lineHeight: 0.94,
              letterSpacing: "-0.02em",
              opacity: headingSpring.opacity,
              transform: to(
                [headingSpring.y, headingSpring.rotate],
                (y, r) => `translateY(${y}px) rotate(${r}deg)`
              ),
            }}
          >
            Cookies please.
          </animated.h2>
          <animated.p
            className="mb-12 text-white/88 gilroy-regular"
            style={{
              fontSize: "clamp(18px, 1.7vw, 20px)",
              opacity: subtitleSpring.opacity,
              transform: subtitleSpring.y.to((y) => `translateY(${y}px)`),
            }}
          >
            so we don't show you the same websites again.
          </animated.p>
          <div className="flex flex-col gap-4 items-center">
            <animated.div
              style={{
              opacity: acceptSpring.opacity,
              transform: acceptSpring.y.to((y) => `translateY(${y}px)`),
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <button
                onClick={() => queueAction("accept")}
                disabled={isAnimating}
                style={{ pointerEvents: "auto" }}
                className={`cookie-cta-primary h-[clamp(68px,6vw,80px)] w-full max-w-[470px] text-black gilroy-semibold text-[clamp(18px,1.8vw,20px)] ${
                  isAcceptPressed ? "is-pressed" : ""
                } ${
                  isAcceptReleasing ? "is-releasing" : ""
                }`}
              >
                <span className="cookie-cta-primary-face">
                  <CookieOutlinedIcon sx={{ fontSize: 28 }} />
                  Hand us the cookies
                </span>
              </button>
            </animated.div>

            <animated.div
              style={{
                opacity: declineSpring.opacity,
                transform: declineSpring.y.to((y) => `translateY(${y}px)`),
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <button
                onClick={() => queueAction("decline")}
                disabled={isAnimating}
                className={`cookie-cta-secondary h-[clamp(58px,5vw,64px)] w-full max-w-[470px] px-4 bg-[#323232] rounded-[999px] text-gray-300 gilroy-regular text-[clamp(17px,1.6vw,18px)] ${
                  isDeclinePressed ? "is-pressed" : ""
                } ${
                  isDeclineReleasing ? "is-releasing" : ""
                }`}
                style={{ pointerEvents: "auto" }}
              >
                Naaah I'm good
              </button>
            </animated.div>
          </div>
        </div>
      </div>

      {/* Dino + asteroid — the world arrives */}
      <animated.div
        style={{
          opacity: worldSpring.opacity,
          transform: worldSpring.y.to((y) => `translateY(${y}px)`),
        }}
      >
        {/* Dino with cycling illustration */}
        <div
          className="absolute left-1/2 -translate-x-1/2"
          style={{
            bottom: `${94 + WORLD_SHIFT_UP_PX}px`,
            zIndex: 2,
          }}
        >
          <img
            src={dinoImage}
            alt="Dino"
            style={{ width: `${DINO_WIDTH_PX}px`, height: `${DINO_WIDTH_PX}px` }}
          />
          <div
            className="absolute animate-oh-shit-float"
            style={{
              left: "138%",
              bottom: `${DINO_WIDTH_PX}px`,
              width: `${OH_SHIT_ITEMS[dinoIndex].w * OH_SHIT_ITEMS[dinoIndex].scale}px`,
              height: `${OH_SHIT_ITEMS[dinoIndex].h * OH_SHIT_ITEMS[dinoIndex].scale}px`,
              cursor: "pointer",
              transition: "width 0.35s ease, height 0.35s ease",
            }}
            onClick={handleDinoClick}
          >
            {OH_SHIT_ITEMS.map((item, i) => {
              const displayW = item.w * item.scale;
              const displayH = item.h * item.scale;
              const isActive = i === dinoIndex;
              return (
                <img
                  key={i}
                  src={item.src}
                  alt={`Dino illustration ${i + 1}`}
                  className="max-w-none"
                  style={{
                    position: "absolute",
                    left: 0,
                    bottom: 0,
                    width: `${displayW}px`,
                    height: `${displayH}px`,
                    opacity: isActive ? 1 : 0,
                    transform: isActive
                      ? "scale(1) rotate(0deg)"
                      : dinoTransitioning
                        ? "scale(0.6) rotate(12deg)"
                        : "scale(0.8) rotate(-8deg)",
                    transition: "opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1), transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
                    transformOrigin: "left bottom",
                    pointerEvents: "none",
                  }}
                />
              );
            })}
          </div>
        </div>

        {/* Asteroid with cookie */}
        <div
          className="absolute left-[35%] -translate-x-1/3 animate-cookie-comet"
          style={{
            bottom: `${160 + WORLD_SHIFT_UP_PX}px`,
            zIndex: 2,
          }}
        >
          <img src={asteroidImage} alt="Asteroid" className="w-31 h-31" />
        </div>

        {/* Semi-circle with glow */}
        <div
          className="absolute left-1/2 -translate-x-1/2"
          style={{ bottom: `${-1416 + WORLD_SHIFT_UP_PX}px` }}
        >
          <div
            style={{
              position: "absolute",
              width: "1516px",
              height: "1516px",
              borderRadius: "50%",
              background: "#FFF",
              filter: "blur(30px)",
              opacity: "0.8",
            }}
          />
          <div
            style={{
              position: "relative",
              width: "1516px",
              height: "1516px",
              borderRadius: "50%",
              background: "#FFF",
              zIndex: 1,
            }}
          />
        </div>
      </animated.div>

      {/* Text overlay — the punchline */}
      <animated.div
        className="absolute left-1/2 z-10 text-center"
        style={{
          bottom: `${WORLD_TEXT_OFFSET_PX + WORLD_SHIFT_UP_PX}px`,
          opacity: bottomTextSpring.opacity,
          // Combine horizontal centering and vertical spring translation
          transform: bottomTextSpring.y.to(
            (y) => `translate(-50%, ${y}px)`
          ),
        }}
      >
        <p className="text-black gilroy-bold">
          We don't make money with your data.
        </p>
        <p className="text-black gilroy-regular">
          Or any money at all actually.
        </p>
      </animated.div>

      {/* Better manifesto — card slides / settles via PeekingManifesto.
          Only mount once the curtain phase is over, and then delay its
          entrance so it’s the last to arrive. */}
      {show && (
        <PeekingManifesto
          cookieEntrance
          entranceDelayMs={MANIFESTO_ENTRANCE_DELAY_MS}
        />
      )}
    </div>
  );
};

export default CookieConsent;
